/**
 * Score Calculation Utilities & Hook
 *
 * Pure functions for score mapping, severity mapping, and status-to-decision
 * conversion. Also provides the useScoreCalculation hook for reactive
 * score delta computation.
 *
 * Extracted from useRevisionTracking.ts for single-responsibility clarity.
 */

import { useMemo, useRef } from 'react';
import { getDramaCategory, type DramaCategory, type IssueSeverity } from '@dramadetector/shared';
import type { RewriteDecision } from '@dramadetector/api-client';
import type { InlineIssue, IssueStatus } from '../types';

/**
 * Map UI IssueStatus to database RewriteDecision
 * @returns RewriteDecision or null for pending status (should not be tracked)
 */
export function mapStatusToDecision(status: IssueStatus): RewriteDecision | null {
  switch (status) {
    case 'accepted':
      return 'accepted';
    case 'ignored':
      return 'ignored';
    case 'pending':
      return null; // Pending status should not be tracked
    default:
      // Type-safe exhaustive check
      const _exhaustiveCheck: never = status;
      throw new Error(`Unknown status: ${_exhaustiveCheck}`);
  }
}

/**
 * Map score to drama category
 * Uses semantic naming aligned with DramaCategory type and database constraints
 *
 * Defensive: Handles NaN/undefined by defaulting to 'caution' (middle ground)
 * This prevents check constraint violations from invalid scores
 */
export function scoreToDramaCategory(score: number): DramaCategory {
  // Guard against NaN/undefined (would cause check constraint violation)
  if (typeof score !== 'number' || Number.isNaN(score)) {
    console.warn('[scoreToDramaCategory] Invalid score, defaulting to caution:', score);
    return 'caution';
  }
  return getDramaCategory(score);
}

/**
 * Map IssueSeverity to database type
 */
export function mapSeverity(severity: IssueSeverity): 'low' | 'medium' | 'high' {
  switch (severity) {
    case 'critical':
    case 'high':
      return 'high';
    case 'medium':
      return 'medium';
    default:
      return 'low';
  }
}

/**
 * Calculate revised score based on issues resolved
 * Simple linear reduction: each issue resolved reduces score by 0.5-1 points
 *
 * @param originalScore - Original score (0-10, clamped)
 * @param issuesResolved - Number of issues resolved (clamped to non-negative)
 * @param totalIssues - Total issues (clamped to non-negative)
 * @returns Revised score (0-10, rounded to 1 decimal)
 */
export function calculateRevisedScore(
  originalScore: number,
  issuesResolved: number,
  totalIssues: number
): number {
  // Clamp inputs to valid ranges for safety
  const clampedScore = Math.max(0, Math.min(10, originalScore));
  const clampedResolved = Math.max(0, issuesResolved);
  const clampedTotal = Math.max(0, totalIssues);

  if (clampedTotal === 0 || clampedResolved === 0) return clampedScore;

  // Each resolved issue reduces score by portion of the total drama
  const reductionPerIssue = clampedScore / clampedTotal;
  const reduction = clampedResolved * reductionPerIssue * 0.8; // 80% reduction per issue

  const revisedScore = clampedScore - reduction;
  return Math.max(0, Math.min(10, Math.round(revisedScore * 10) / 10));
}

/** Minimum accepted rewrites before triggering server rescore */
export const RESCORE_THRESHOLD = 2;

/** Debounce delay (ms) before calling the rescore endpoint */
export const RESCORE_DEBOUNCE_MS = 1000;

interface UseScoreCalculationOptions {
  issues: InlineIssue[];
  originalScore: number;
  totalIssues: number;
}

interface UseScoreCalculationReturn {
  /** Calculated revised score (0-10) */
  revisedScore: number;
  /** Score improvement delta (originalScore - revisedScore, positive = improvement) */
  scoreDelta: number;
  /** Mutable ref holding server-side score (null until rescore completes) */
  serverScoreRef: React.RefObject<number | null>;
  /** Mutable ref holding server-side category */
  serverCategoryRef: React.RefObject<DramaCategory | null>;
}

/**
 * Hook for reactive score delta computation.
 *
 * DD-162: Prefers server-side score when available; falls back to client estimate.
 */
export function useScoreCalculation({
  issues,
  originalScore,
  totalIssues,
}: UseScoreCalculationOptions): UseScoreCalculationReturn {
  // Server-side rescore state (DD-162)
  // Intentionally non-reactive to avoid unnecessary re-renders during the debounce window
  const serverScoreRef = useRef<number | null>(null);
  const serverCategoryRef = useRef<DramaCategory | null>(null);

  const { revisedScore, scoreDelta } = useMemo(() => {
    const issuesAccepted = issues.filter((i) => i.status === 'accepted').length;

    // Use server score if available (set by rescore endpoint)
    if (serverScoreRef.current !== null) {
      const roundedOriginal = Math.round(originalScore);
      return {
        revisedScore: serverScoreRef.current,
        scoreDelta: roundedOriginal - serverScoreRef.current,
      };
    }

    // Fall back to client-side estimation
    const calculated = calculateRevisedScore(originalScore, issuesAccepted, totalIssues);
    // Round to integer for display (consensus decision: reduce cognitive load)
    const roundedRevised = Math.round(calculated);
    const roundedOriginal = Math.round(originalScore);
    return {
      revisedScore: roundedRevised,
      scoreDelta: roundedOriginal - roundedRevised,
    };
  }, [issues, originalScore, totalIssues]);

  return { revisedScore, scoreDelta, serverScoreRef, serverCategoryRef };
}
